import type { FrameMessage, StatusOrigin } from "./frame-messages";
import type { BuyerSession } from "./state-machine";
import type {
  BankPage,
  ConfirmPayment,
  DeviceSession,
  PaymentReason,
  PrepareDeviceSession,
} from "./card-island";
import type { BuyerWalletPayment, WalletApi } from "./wallet-api";

/**
 * Everything the checkout core reaches outside itself, as narrow interfaces
 * a renderer hands in. The core never names a framework, a processor or the
 * DOM: the web renderer passes the buyer client, identity (the host's tokens
 * or the identity SDK), Stripe (for the charge) and the frame bridge, and
 * hands over the card island at pay; a test passes fakes and drives time by
 * hand.
 */

// ---- the engine --------------------------------------------------------------

/** A buyer-plane answer: the session, or the engine's error envelope. */
export type EngineResult<T> = { data?: T; error?: unknown };

/** The four buyer-plane calls the checkout makes on the session itself. */
export type BuyerPlane = {
  get: (sessionId: string) => Promise<EngineResult<BuyerSession>>;
  update: (
    sessionId: string,
    body: { customer: { email: string; name: null } },
  ) => Promise<EngineResult<BuyerSession>>;
  complete: (sessionId: string) => Promise<EngineResult<BuyerSession>>;
  /** The money moved: the engine settles now, reading the payment from the
   *  processor itself, and answers the session as `get` does. Safe to ask
   *  again; it trusts nothing the checkout says. */
  finalize: (sessionId: string) => Promise<EngineResult<BuyerSession>>;
};

/** The engine, as far as this session goes. Either half may be missing (no
 *  API origin configured): the checkout then says it is unavailable. */
export type EnginePort = {
  buyer: BuyerPlane | null;
  wallet: WalletApi | null;
};

// ---- identity ----------------------------------------------------------------

/** A one-time code the identity plane has sent and is waiting for. */
export type CodeRequest = {
  kind: "token" | "login";
  maskedEmail: string;
  resendAt: number;
};

/** What the checkout's own identity broker knows about this browser. */
export type IdentityView = {
  /** A client exists to ask: without one there are no Lissen surfaces. */
  connected: boolean;
  status: "loading" | "anonymous" | "known" | "recognised" | "failed";
  maskedEmail: string | null;
  consentsHeld: ReadonlyArray<string>;
  pending: CodeRequest | null;
};

/** The view when there is no broker at all: the host answers for the buyer. */
export const NO_BROKER: IdentityView = {
  connected: false,
  status: "anonymous",
  maskedEmail: null,
  consentsHeld: [],
  pending: null,
};

/** An action token for this session, with the consents it should carry. */
export type TokenRequest = {
  sessionId: string;
  consents?: Array<string>;
  rememberMe?: boolean;
  /** The address to send a code to when the browser is not recognised
   *  (the broker's code step only). */
  email?: string;
  /** A recognised session proves itself again before a wallet payment. */
  reverify?: boolean;
};

/** A verified token for this session, or a code the broker is now waiting
 *  for (its step then follows the broker's view). Failure throws. */
export type TokenAnswer = { token: string } | { code: CodeRequest | null };

/**
 * The checkout's own identity connection (the SDK's hidden broker frame):
 * who this browser is, the in-place code step, and signing out. Only a
 * checkout nobody hands tokens to has one — partner embeds and the hosted
 * page. On Link in Bio and in the Lissen app the host answers instead.
 */
export type BrokerPort = {
  current: () => IdentityView;
  subscribe: (listener: () => void) => () => void;
  verifyCode: (code: string) => Promise<{ token: string | null }>;
  resendCode: () => Promise<void>;
  /** Put a pending code away (the buyer chose to continue as a guest). */
  cancelCode: () => Promise<void>;
  /** Sign this browser out of Lissen. */
  forget: () => Promise<void>;
};

/**
 * Identity, as one function: `checkoutToken` is the only way a token comes
 * into the core — the silent attestation, a new card's card-reuse consent at
 * pay and the renewed proof alike. Whoever answers it (the host, or the
 * broker) is the port's business. `broker` is there only when the checkout
 * runs its own identity connection; the reactions branch on whether it is.
 */
export type IdentityPort = {
  checkoutToken: (request: TokenRequest) => Promise<TokenAnswer>;
  /** The host answers first: a token it handed over (or promised) is tried
   *  before anything else resolves the buyer. Always so without a broker. */
  handed?: boolean;
  broker?: BrokerPort;
};

/** Card reuse across organizers, held against the Lissen wallet itself. */
export const CARD_REUSE_CONSENT = "card_reuse:lissen:card";

// ---- payments ------------------------------------------------------------------

// The card island's contract (card-island.ts): processor-neutral,
// so the core may name it. The core never holds the card itself.
export type {
  BankPage,
  CardIsland,
  CardResult,
  DeviceSession,
  PaymentAccount,
  PrepareDeviceSession,
} from "./card-island";

/**
 * The payment processor, neutral: a payment the engine prepared is
 * confirmed, which may need the bank's check under `reason` — drawn through
 * `bank` by a host that draws it, or by the island itself on its own page.
 * It throws with the buyer's sentence. Both the card typed now and this
 * confirmation are the card island's (card-island.ts): `paymentsFromIsland`
 * makes this port of its `ConfirmPayment`.
 *
 * `deviceSession`, where the renderer can make one (the web checkout), lets
 * the engine confirm a saved card's payment itself, one round trip sooner
 * (pay-flows.ts); `prepareDeviceSession` has it begun once a saved card is
 * on offer, before the press (checkout-reactions.ts). Without them every
 * payment is confirmed here, as the app does.
 */
export type PaymentsPort = {
  confirmPayment: (input: {
    payment: BuyerWalletPayment;
    reason: PaymentReason;
    bank: BankPage;
  }) => Promise<{ intentId: string }>;
  deviceSession?: DeviceSession;
  prepareDeviceSession?: PrepareDeviceSession;
};

/** The payments port over the card island's confirmation, wherever the
 *  island runs: its refusal becomes the error the core shows. */
export function paymentsFromIsland(
  confirm: ConfirmPayment,
  deviceSession?: DeviceSession,
  prepareDeviceSession?: PrepareDeviceSession,
): PaymentsPort {
  return {
    async confirmPayment(input) {
      const result = await confirm(input);
      if ("error" in result) throw new Error(result.error);
      return { intentId: result.paymentIntentId };
    },
    ...(deviceSession ? { deviceSession } : {}),
    ...(prepareDeviceSession ? { prepareDeviceSession } : {}),
  };
}

// ---- the page and time ---------------------------------------------------------

/** The page around the checkout: lifecycle messages, and where the status
 *  was last drawn (so a host's success screen can take it over in place). */
export type EventsPort = {
  emit: (message: FrameMessage) => void;
  statusOrigin: () => StatusOrigin | null;
};

export type TimerHandle = unknown;

/** Time, injected so a test can move it by hand. */
export type Clock = {
  now: () => number;
  setTimeout: (run: () => void, ms: number) => TimerHandle;
  clearTimeout: (handle: TimerHandle) => void;
};
